import { Injectable, ElementRef } from '@angular/core';
import { ActivatedRoute, Router, NavigationStart, NavigationEnd, RouterLinkActive } from '@angular/router';
import { Location } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { ApiService } from './api.service';
import { compact, map, isUndefined, orderBy, forEach } from 'lodash';
import 'rxjs/add/operator/toPromise';

@Injectable()
export class NavigationService {
  private rla;
  private navigationEl;
  private _leftMenu;
  private _rightMenu;
  private _activeNavItemOffset: number = 0;
  private _secondNavItemOffset: number = 0;
  private _opaque: boolean = true;
  private _isSticky: boolean = false;
  private _isTransparent: boolean = false;
  private _isMobileMenuOpen: boolean = false;
  private _navbarHeight: number;
  private _isHidden: boolean = false;
  private _isHiddenDesktop: boolean = false;
  private _closeMenuOnNavigation: boolean = false;
  private _parentSlug: string;
  private _projectThumbs: any = [];
  private _projectSource: string = null;
  private _pageType: string;
  private _slugs: any;
  private _slugsObs: BehaviorSubject<any> = null;
  public readonly slugsObs: Observable<any>;
  locationSubscription;
  navigationSubscription;
  backUrl: string = null;
  projectHasBeenClosed = false;
  isBack = false;

  constructor(
    private apiService: ApiService,
    private location: Location,
    private router: Router,
    private meta: Meta,
    private title: Title
   ) {
    this._slugsObs = new BehaviorSubject({
      parent: null,
      child: null
    });
    this.slugsObs = this._slugsObs.asObservable();

    this.location.subscribe((e) => {
      this.backUrl = e.url;
    });

    router.events
      .filter(event => event instanceof NavigationEnd)
      .map(() => router.routerState.snapshot.url)
      .subscribe((url) => {
        this.isBack = (this.backUrl !== null && this.backUrl === url) || this.projectHasBeenClosed;
        this.backUrl = null;
        // Only reset if not index redirect
        this.projectHasBeenClosed = this.projectHasBeenClosed && url === '/project-index';
      });
  }

  onGalleryClose() {
    this.isBack = true;
  }

  onProjectClose() {
    this.projectHasBeenClosed = true;
  }

  get secondNavItemOffset(): number {
    return this._secondNavItemOffset;
  }
    
  set secondNavItemOffset(secondNavItemOffset: number) {
    this._secondNavItemOffset = secondNavItemOffset;
  }

  get activeNavItemOffset(): number {
    return this._activeNavItemOffset;
  }
    
  set activeNavItemOffset(activeNavItemOffset: number) {
    this._activeNavItemOffset = activeNavItemOffset;
  }

  get navbarHeight(): number {
    return this._navbarHeight;
  }
    
  set navbarHeight(navbarHeight: number) {
    this._navbarHeight = navbarHeight;
  }

  get closeMenuOnNavigation(): boolean {
    return this._closeMenuOnNavigation;
  }
    
  set closeMenuOnNavigation(closeMenuOnNavigation: boolean) {
    this._closeMenuOnNavigation = closeMenuOnNavigation;
  }

  get slugs(): any {
    return this._slugs;
  }
    
  set slugs(slugs: any) {
    this._slugsObs.next(slugs);
    this._slugs = slugs;
  }

  get projectThumbs(): any {
    return this._projectThumbs;
  }
    
  set projectThumbs(projectThumbs: any) {
    this._projectThumbs = projectThumbs;
  }

  get projectSource(): string {
    return this._projectSource;
  }
    
  set projectSource(projectSource: string) {
    this._projectSource = projectSource;
  }

  get pageType(): string {
    return this._pageType;
  }
    
  set pageType(pageType: string) {
    this._pageType = pageType;
  }

  get leftMenu(): any[] {
    return this._leftMenu;
  }
    
  set leftMenu(leftMenu: any[]) {
    this._leftMenu = leftMenu;
  }

  get rightMenu(): any[] {
    return this._rightMenu;
  }
    
  set rightMenu(rightMenu: any[]) {
    this._rightMenu = rightMenu;
  }

  get opaque(): boolean {
    return this._opaque;
  }
    
  set opaque(opaque: boolean) {
    this._opaque = opaque;
  }

  get isSticky(): boolean {
    return this._isSticky;
  }
    
  set isSticky(isSticky: boolean) {
    this._isSticky = isSticky;
  }

  get isMobileMenuOpen(): boolean {
    return this._isMobileMenuOpen;
  }
    
  set isMobileMenuOpen(isMobileMenuOpen: boolean) {
    this._isMobileMenuOpen = isMobileMenuOpen;
  }

  get isHidden(): boolean {
    return this._isHidden;
  }
    
  set isHidden(isHidden: boolean) {
    this._isHidden = isHidden;
  }

  get isHiddenDesktop(): boolean {
    return this._isHiddenDesktop;
  }
    
  set isHiddenDesktop(isHiddenDesktop: boolean) {
    this._isHiddenDesktop = isHiddenDesktop;
  }

  get isTransparent(): boolean {
    return this._isTransparent;
  }
    
  set isTransparent(isTransparent: boolean) {
    this._isTransparent = isTransparent;
  }

  setTitleAndMetaDescription(apiData, defaultTitle = null) {
    let _title = 'Danny Lane';
    let _metaTags;
    let _metaDescription;

    if (typeof apiData !== 'undefined' && typeof apiData.title !== 'undefined' && apiData.title !== '') {
      _title += ' - ' + apiData.title;
    } else if (typeof defaultTitle !== 'undefined' && defaultTitle !== null) {
      _title += ' - ' + defaultTitle;
    }

    if (typeof apiData !== 'undefined') {
      _metaTags = apiData.meta_tags;
      _metaDescription = apiData.meta_description;
    }
    
    this.title.setTitle(_title);

    if (typeof _metaDescription !== 'undefined' && _metaDescription !== '') {
      this.meta.addTags([
        { name: 'description', content: _metaDescription }
      ]);
    } else {
      this.meta.removeTag('description');
    }
    
    window.scrollTo(0, 0);
  }

  isThreeColPage(page) {
    return page.page_type === 'column'
  }

  getThreeColPageChildrenNavItems(page: any) {
    return compact(map(page.cols, (item: any) => {
      return isUndefined(item.slug) ? null : {
        parent_id: page.id,
        slug: item.slug,
        title: item.title,
        parent_position: item.col * 100 + item.position
      }
    }));
  }

  getOrderedMenu(menu) {
    return new Promise((resolve, reject) => {
      let parentPagesWithId = [];
      let _menu = orderBy(menu, ['position'], ['asc']);
      _menu = map(_menu, (item: any) => {
        let children;
        if (item.page_id !== 0) {
          parentPagesWithId.push(item);
        } else {
          children = item.children;
          item.children = orderBy(children, ['parent_position'], ['asc']);
        }
        return item;
      });

      let reqsForParentPagesWithId = map(parentPagesWithId, (item) => {
        return this.apiService.getData(`/pages/${item.page_id}`);
      });

      // if (reqsForParentPagesWithId.length > 0) {
      //   Promise.all(reqsForParentPagesWithId).then((resp) => {
      //     forEach(resp, (data, index) => {
      //       let page = data['pages'][0];
      //       if (this.isThreeColPage(page)) {
      //         parentPagesWithId[index].children = this.getThreeColPageChildrenNavItems(page);
      //       }
      //     });
      //     resolve(_menu);
      //   });
      // } else {
        resolve(_menu);
      // }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/navigation.service.ts